import { Navigate } from "react-router-dom";

import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { useTenantSwitcher } from "@/contexts/TenantSwitcherContext";
import { useAuth } from "@/contexts/AuthContext";

export function RootRedirect(): JSX.Element {
  const { user, isLoading } = useAuth();
  const { activeTenant, loadingTenant } = useTenantSwitcher();

  if (isLoading || loadingTenant) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingLogo size="lg" />
      </div>
    );
  }

  const isPlatform = user?.platformRole === "root" || user?.platformRole === "root_admin";
  if (isPlatform && !activeTenant) {
    return <Navigate to="/tenants" replace />;
  }

  // Accountants land on the aggregate clients view by default. accountType
  // is the durable signal (the session role becomes "owner" when an
  // accountant is the owner of a client tenant they manage). Either signal
  // is enough to flag the user as an accountant.
  const isAccountant =
    user?.accountType === "accountant" || user?.role === "accountant";
  if (!isPlatform && isAccountant) {
    return <Navigate to="/accountant" replace />;
  }

  const onboardingComplete = user?.onboardingCompletedAt != null;
  const hasTenant = user?.tenantId != null;
  if (!isPlatform && user != null && !hasTenant) {
    return <Navigate to="/onboarding" replace />;
  }
  if (!isPlatform && hasTenant && !onboardingComplete) {
    // Delegated handoff: a business owner who chose "my accountant
    // continues" and has resolved the plan gate (planId set) is DONE with
    // their part — the accountant finishes onboarding from their cartera.
    // Send them straight to the app instead of trapping them in the wizard.
    // Without a plan yet they still belong in /onboarding (the plan gate).
    const isDelegatedOwner =
      user?.onboardingMode === "accountant" &&
      user?.accountType !== "accountant" &&
      user?.planId != null;
    if (isDelegatedOwner) {
      return <Navigate to="/dashboard" replace />;
    }
    return <Navigate to="/onboarding" replace />;
  }

  return <Navigate to="/dashboard" replace />;
}
